import React from "react";
import { Badge } from "react-bootstrap";
import {
  FaCalendarAlt, FaInfoCircle, FaGlobe
} from "react-icons/fa";
import GenericCrudPage from '../components/GenericCrudPage'; // Assuming this path is correct
import DatePicker from "react-datepicker"; // DatePicker is still needed for its component
import "react-datepicker/dist/react-datepicker.css"; // DatePicker styles

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Validation for academic year form
const validateAcademicYearForm = (form) => {
  const errors = {};
  if (!form.exYear.trim()) errors.exYear = "Academic Year is required";
  if (form.exYear.length > 50) errors.exYear = "Academic Year must be less than 50 characters";
  if (!form.exStartdate) errors.exStartdate = "Start Date is required";
  if (!form.exEnddate) errors.exEnddate = "End Date is required";
  if (form.exStartdate && form.exEnddate && form.exStartdate > form.exEnddate) {
    errors.exEnddate = "End Date cannot be before Start Date";
  }
  return errors;
};

// Configuration for Academic Year CRUD operations
const academicYearConfig = {
  baseUrl: BASE_URL,
  endpoints: {
    fetch: '/master/getAcademicYear',
    save: '/master/saveAcademicYear',
    update: '/master/updateAcademicYear',
    delete: '/master/deleteAcademicYear'
  },
  validateForm: validateAcademicYearForm,
  initialFormState: {
    exYear: "", exDescription: "", exStartdate: null, exEnddate: null, exYearInLocalLang: "", exIsActive: 1, user: ""
  },
  entityName: 'Academic Year'
};

// Form fields configuration
const formFields = [
  { name: "exYear", label: "Academic Year", icon: FaCalendarAlt, required: true },
  { name: "exYearInLocalLang", label: "Local Language Year", icon: FaGlobe },
  // DatePicker fields: Assuming GenericCrudPage can render DatePicker based on 'type: "date"'
  // and pass a unified handleChange that accepts Date objects.
  { name: "exStartdate", label: "Start Date", icon: FaCalendarAlt, required: true, type: "date", component: DatePicker },
  { name: "exEnddate", label: "End Date", icon: FaCalendarAlt, required: true, type: "date", component: DatePicker },
  { name: "exDescription", label: "Description", icon: FaInfoCircle, as: "textarea", rows: 3 },
  { name: 'exIsActive', label: 'Active Status', as: 'switch' },
];

// Table columns configuration
const tableColumns = [
  {
    key: 'exYear',
    label: 'Academic Year',
    sortable: true,
    render: (academicYear) => (
      <div>
        <div className="fw-bold">{academicYear.exYear}</div>
        <div className="text-muted small">{academicYear.exYearInLocalLang || 'No local name'}</div>
      </div>
    )
  },
  { key: 'exStartdate', label: 'Start Date', sortable: true },
  { key: 'exEnddate', label: 'End Date', sortable: true },
];

// CSV export headers
const csvHeaders = [
  { key: 'exYear', label: 'Academic Year' },
  { key: 'exDescription', label: 'Description' },
  { key: 'exStartdate', label: 'Start Date' },
  { key: 'exEnddate', label: 'End Date' },
  { key: 'exYearInLocalLang', label: 'Local Language Year' },
  { key: 'exIsActive', label: 'Status' }
];

// Transform form data to API payload
// The userId is passed from GenericCrudPage
const transformPayload = (form, editId, userId) => ({
  exYear: form.exYear.trim(),
  exDescription: form.exDescription.trim(),
  exStartdate: form.exStartdate ? form.exStartdate.toLocaleDateString("en-GB") : null, // Format date for API
  exEnddate: form.exEnddate ? form.exEnddate.toLocaleDateString("en-GB") : null,     // Format date for API
  exYearInLocalLang: form.exYearInLocalLang.trim(),
  exIsActive: form.exIsActive ? 1 : 0, // Convert boolean to 1 or 0 for the API
  user: userId, // Ensure user is passed
  ...(editId && { id: editId })
});

// A more robust date parser that returns a valid Date object or null
const parseApiDate = (dateString) => {
  // Return null if the input is falsy (null, undefined, "")
  if (!dateString) {
    return null;
  }

  // Check if it's already a valid Date object
  if (dateString instanceof Date && !isNaN(dateString)) {
    return dateString;
  }

  // Check for "dd/MM/yyyy" format from the API
  const parts = String(dateString).split('/');
  if (parts.length === 3) {
    const [day, month, year] = parts.map(Number);
    // Create date and check if it's valid. Month is 0-indexed.
    const date = new Date(year, month - 1, day);
    if (date && date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day) {
      return date;
    }
  }
  
  // As a fallback, try parsing it directly (handles ISO strings like YYYY-MM-DD)
  const fallbackDate = new Date(dateString);
  if (fallbackDate instanceof Date && !isNaN(fallbackDate)) {
    return fallbackDate;
  }

  // If all parsing fails, log a warning and return null
  console.warn(`Could not parse the date: "${dateString}"`);
  return null;
};

// Transform API data to form
const transformToForm = (academicYear) => {
  return {
    exYear: academicYear.exYear || "",
    exDescription: academicYear.exDescription || "",
    exStartdate: parseApiDate(academicYear.exStartdate),
    exEnddate: parseApiDate(academicYear.exEnddate),
    exYearInLocalLang: academicYear.exYearInLocalLang || "", 
    exIsActive: academicYear.exIsActive === 1 || academicYear.exIsActive === true, // Ensure value is boolean
    user: academicYear.user || "", // User ID from fetched data
  };
};

// Get view modal fields
const getViewFields = (academicYear) => {
  if (!academicYear) return [];
  return [
    { label: "Academic Year", value: academicYear.exYear, icon: FaCalendarAlt },
    { label: "Local Language Year", value: academicYear.exYearInLocalLang, icon: FaGlobe },
    { label: "Start Date", value: academicYear.exStartdate, icon: FaCalendarAlt },
    { label: "End Date", value: academicYear.exEnddate, icon: FaCalendarAlt },
    { label: "Description", value: academicYear.exDescription, icon: FaInfoCircle, fullWidth: true },
  ];
};

export default function AcademicYearPage() {
  return (
    <GenericCrudPage
      title="Academic Years" // This will be the header text
      config={academicYearConfig}
      formFields={formFields}
      tableColumns={tableColumns}
      emptyIcon={FaCalendarAlt}
      searchPlaceholder="Search by year, description, local language..."
      csvHeaders={csvHeaders}
      csvFilename="academic_years_export.csv"
      transformPayload={transformPayload}
      transformToForm={transformToForm}
      getViewFields={getViewFields}
      // You might need to pass DatePicker component if GenericCrudPage doesn't import it
      // For example: customComponents={{ DatePicker }}
    />
  );
}